/**
 * CockpitFrame — generic Cockpit chrome wrapper for embedding existing pages.
 *
 * For pages that don't warrant a full structural rewrite, this frame provides
 * the visible Cockpit chrome (Masthead + optional KpiStrip) with the
 * embedded page rendered inside `.cockpit-surface` so the ambient cockpit
 * styles cascade.
 *
 * Use this for admin pages, single-purpose dashboards, and detail surfaces
 * where the interactive content should remain unchanged.
 */
import { ReactNode } from "react";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip,
} from "@/components/cockpit/CockpitShell";

interface FrameMetric {
  label: string;
  value: string | number;
  hero?: boolean;
  tone?: "default" | "good" | "warn" | "bad";
}

interface FrameKpi {
  label: string;
  value: string | number;
  tone?: "default" | "good" | "warn" | "bad";
  hint?: string;
}

interface CockpitFrameProps {
  eyebrow?: string;
  title: string;
  metrics?: FrameMetric[];
  kpis?: FrameKpi[];
  rightSlot?: ReactNode;
  children: ReactNode;
}

export function CockpitFrame({ eyebrow, title, metrics, kpis, rightSlot, children }: CockpitFrameProps) {
  const hasMetrics = metrics && metrics.length > 0;
  const right = (hasMetrics || rightSlot) ? (
    <>
      {hasMetrics && metrics!.map((m, i) => (
        <span key={`${m.label}-${i}`} className="contents">
          {i > 0 && <HeroDivider />}
          <HeroMetric label={m.label} value={m.value} hero={m.hero} tone={m.tone} />
        </span>
      ))}
      {rightSlot}
    </>
  ) : undefined;
  return (
    <CockpitPage>
      <CockpitMasthead
        eyebrow={eyebrow}
        title={title}
        right={right}
      />
      {kpis && kpis.length > 0 && <CockpitKpiStrip items={kpis} />}
      <div className="cockpit-surface -mx-6">
        <div className="px-6">{children}</div>
      </div>
    </CockpitPage>
  );
}
